// The editor's checks. The same checks run on a hand edit and on a Claude
// draft, and Publish runs them again; a revision moves to Ready for check only
// when every one passes. The rules come from rules.json.

import { parseInline } from './inline';
import defaultRules from './rules.json';
import type { ArticleContent, HelpBlock, MediaInfo, MediaKind, SafetyKind } from './types';

export interface Rules {
  summaryMax: number;
  clipMaxBytes: number;
  banned: {
    tier1: string[];
    tier2: string[];
    exempt: Record<string, string>;
    shapes: { pattern: string; message: string }[];
  };
  protected: string[];
  names: string[];
  safetyLines: Record<SafetyKind, string | null>;
}

export const RULES = defaultRules as Rules;

export type CheckId = 'labels' | 'words' | 'summary' | 'media' | 'links' | 'clips' | 'video' | 'safety';

export interface Problem {
  text: string;
  /** The source row that holds the label's exact text. */
  docId?: string;
  /** The exact text to use instead. */
  use?: string;
  /** A label that may be ticked as seen on the Scout. */
  label?: string;
}

export interface CheckResult {
  id: CheckId;
  ok: boolean;
  message: string;
  problems: Problem[];
}

export interface CheckContext {
  /** The revision's source rows, as they read when it was written. */
  rows: { docId: string; rowText: Record<string, string> }[];
  media: Record<string, MediaInfo>;
  /** Articles a link may point at: they exist and are not retired. */
  liveTopics: ReadonlySet<string>;
  rules?: Rules;
}

const BLOCK_NAMES: Record<HelpBlock['type'], string> = {
  paragraph: 'Paragraph', subheading: 'Subheading', note: 'Note', steps: 'Steps', image: 'Image',
  clip: 'Clip', video: 'Video', table: 'Table', link: 'Link',
};

/** One piece of text and where it is, as the editor names it. */
interface Located {
  where: string;
  text: string;
  /** Title, subheading and table heading text must be in sentence case. */
  heading?: boolean;
  /** A table cell, where a lone em dash marks an empty reading. */
  cell?: boolean;
}

const blockName = (body: HelpBlock[], i: number) => `${BLOCK_NAMES[body[i].type]} ${i + 1}`;

function stepName(body: HelpBlock[], i: number, step: number) {
  const many = body.filter((b) => b.type === 'steps').length > 1;
  return many ? `Step ${step + 1} of ${blockName(body, i)}` : `Step ${step + 1}`;
}

function texts(a: ArticleContent): Located[] {
  const out: Located[] = [
    { where: 'Title', text: a.title, heading: true },
    { where: 'Summary', text: a.summary },
  ];
  if (a.whereToFind) out.push({ where: 'Where to find it', text: a.whereToFind });
  if (a.requirements) out.push({ where: 'Needs', text: a.requirements });
  a.body.forEach((b, i) => {
    const where = blockName(a.body, i);
    switch (b.type) {
      case 'paragraph': case 'note': out.push({ where, text: b.text }); break;
      case 'subheading': out.push({ where, text: b.text, heading: true }); break;
      case 'steps': b.items.forEach((s, n) => out.push({ where: stepName(a.body, i, n), text: s.text })); break;
      case 'image': case 'clip': if (b.caption) out.push({ where: `${where} caption`, text: b.caption }); break;
      case 'table':
        b.head.forEach((h) => out.push({ where, text: h, heading: true }));
        b.rows.forEach((r) => r.forEach((c) => out.push({ where, text: c, cell: true })));
        break;
      case 'link': if (b.text) out.push({ where, text: b.text }); break;
      case 'video': break;
    }
  });
  return out;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A whole-word match: no letter, digit, apostrophe or hyphen either side. */
const wordRe = (phrase: string, flags = 'u') => new RegExp(`(?<![\\p{L}\\p{N}'-])${escapeRe(phrase)}(?![\\p{L}\\p{N}'-])`, flags);

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

function result(id: CheckId, ok: string, failing: string, problems: Problem[]): CheckResult {
  return { id, ok: problems.length === 0, message: problems.length === 0 ? ok : failing, problems };
}

function labelsCheck(a: ArticleContent, ctx: CheckContext): CheckResult {
  const problems: Problem[] = [];
  const seen = new Set<string>();
  const values = ctx.rows.flatMap((r) => Object.values(r.rowText).map((v) => ({ docId: r.docId, v })));
  for (const t of texts(a)) {
    const parsed = parseInline(t.text);
    if (parsed.error) { problems.push({ text: `${t.where}: ${lowerFirst(parsed.error)}` }); continue; }
    for (const s of parsed.spans) {
      if (!s.bold || seen.has(s.text)) continue;
      seen.add(s.text);
      if (a.seenLabels.includes(s.text)) continue;
      if (values.some(({ v }) => wordRe(s.text).test(v))) continue;
      const near = values.map(({ docId, v }) => ({ docId, m: wordRe(s.text, 'iu').exec(v) })).find((x) => x.m);
      problems.push(near
        ? { text: `${s.text} is not in any source row. ${near.docId} reads ${near.m![0]}.`, docId: near.docId, use: near.m![0], label: s.text }
        : { text: `${s.text} is not in any source row.`, label: s.text });
    }
  }
  return result('labels', 'Every bold label is in a source row or seen on the Scout', 'A bold label needs a source row, or a tick that it was seen on the Scout', problems);
}

/** The words that break sentence case ([] when none), or 'lower-start'; names and labels keep their own case. */
function titleCased(text: string, names: string[]): string[] | 'lower-start' {
  let s = parseInline(text).spans.map((x) => (x.bold ? ' Ⓝ ' : x.text)).join('');
  for (const n of [...names].sort((x, y) => y.length - x.length)) s = s.replace(new RegExp(wordRe(n).source, 'gu'), 'Ⓝ');
  const tokens = [...s.matchAll(/Ⓝ|[\p{L}\p{N}][\p{L}\p{N}'’+-]*/gu)];
  const bad: string[] = [];
  tokens.forEach((m, i) => {
    const w = m[0];
    if (w === 'Ⓝ') return;
    const before = s.slice(0, m.index).trimEnd();
    const starts = i === 0 || /[.?!:]$/.test(before);
    if (i === 0 && /^\p{Ll}/u.test(w)) { bad.push('\u0000'); return; }
    if (starts || !/^\p{Lu}/u.test(w)) return;
    // The pronoun I, and I'm, I've, I'd, I'll, are capitals in any sentence.
    if (/^I(['’](m|ve|d|ll))?$/u.test(w)) return;
    const letters = w.replace(/[^\p{L}]/gu, '');
    if (/\p{N}/u.test(w) || (letters.length >= 2 && letters === letters.toUpperCase())) return;
    bad.push(w);
  });
  if (bad[0] === '\u0000') return 'lower-start';
  return bad;
}

function wordsCheck(a: ArticleContent, rules: Rules): CheckResult {
  const problems: Problem[] = [];
  const banned = [...rules.banned.tier1, ...rules.banned.tier2].filter((w) => !(w in rules.banned.exempt));
  const reported = new Set<string>();
  for (const t of texts(a)) {
    const parsed = parseInline(t.text);
    let plain = parsed.spans.map((x) => (x.bold ? ' '.repeat(x.text.length + 4) : x.text)).join('').replace(/’/g, "'");
    for (const p of rules.protected) plain = plain.split(p).join(' '.repeat(p.length));
    const here: Problem[] = [];

    const hits = banned
      .map((w) => ({ w, m: wordRe(w, 'iu').exec(plain) }))
      .filter((h) => h.m)
      .sort((x, y) => x.m!.index - y.m!.index);
    for (const h of hits) here.push({ text: `${t.where}: "${h.w}" is a banned word` });

    const lone = t.cell && t.text.trim() === '—';
    if (!lone && (/[–—]/.test(plain) || / - /.test(plain))) here.push({ text: `${t.where}: no dashes in sentences; use a full stop, comma or colon` });
    if (plain.includes('!')) here.push({ text: `${t.where}: no exclamation marks` });
    for (const shape of rules.banned.shapes) if (new RegExp(shape.pattern, 'iu').test(plain)) here.push({ text: `${t.where}: ${shape.message}` });
    if (!parsed.error && plain.includes('*')) here.push({ text: `${t.where}: no asterisks; bold is **two** on each side` });

    if (t.heading) {
      const tc = titleCased(t.text, rules.names);
      if (tc === 'lower-start') here.push({ text: `${t.where}: start with a capital letter` });
      else if (tc.length) here.push({ text: `${t.where}: sentence case, not title case (${tc.map((w) => `"${w}"`).join(', ')})` });
    }

    for (const p of here) {
      if (reported.has(p.text)) continue;
      reported.add(p.text);
      problems.push(p);
    }
  }
  return result('words', 'No banned words, sentence case, no dashes', 'Fix the wording', problems);
}

function summaryCheck(a: ArticleContent, rules: Rules): CheckResult {
  const s = a.summary.trim();
  const n = [...s].length;
  const problems: Problem[] = [];
  if (n > rules.summaryMax) problems.push({ text: `The summary is ${n} characters; the limit is ${rules.summaryMax}` });
  if (/[.?!]\s+\S/.test(s)) problems.push({ text: 'The summary is more than one sentence' });
  if (!s.endsWith('.')) problems.push({ text: 'The summary ends with a full stop' });
  return result('summary', `Summary is one sentence, ${n} of ${rules.summaryMax}`, `The summary needs one sentence of at most ${rules.summaryMax} characters`, problems);
}

/** Every file the body uses, where, and the kinds allowed there. */
function mediaUses(body: HelpBlock[]): { where: string; mediaId: string; kinds: MediaKind[] }[] {
  const out: { where: string; mediaId: string; kinds: MediaKind[] }[] = [];
  body.forEach((b, i) => {
    if (b.type === 'image' || b.type === 'clip' || b.type === 'video') out.push({ where: blockName(body, i), mediaId: b.mediaId, kinds: [b.type] });
    if (b.type === 'steps') b.items.forEach((s, n) => { if (s.mediaId) out.push({ where: stepName(body, i, n), mediaId: s.mediaId, kinds: ['image', 'clip'] }); });
  });
  return out;
}

function mediaCheck(a: ArticleContent, ctx: CheckContext): CheckResult {
  const problems: Problem[] = [];
  for (const u of mediaUses(a.body)) {
    const m = ctx.media[u.mediaId];
    if (!m) { problems.push({ text: `${u.where}: the file is missing` }); continue; }
    if (!u.kinds.includes(m.kind)) { problems.push({ text: `${u.where}: the file is ${m.kind === 'image' ? 'an' : 'a'} ${m.kind}, not ${u.kinds.length > 1 ? 'an image or clip' : `a ${u.kinds[0]}`}` }); continue; }
    if (!m.alt.trim()) problems.push({ text: `${u.where} has no alt text` });
    if (!m.noPersonalData) problems.push({ text: `${u.where}: tick that the ${m.kind} shows no personal data` });
    if (m.kind === 'clip' && !m.stillSrc) problems.push({ text: `${u.where} has no still frame for reduced motion` });
  }
  return result('media', 'Every image has alt text and the no-personal-data tick', 'Every image needs alt text and the no-personal-data tick', problems);
}

const mb = (bytes: number) => String(Math.round(bytes / 1e5) / 10);

function clipsCheck(a: ArticleContent, ctx: CheckContext, rules: Rules): CheckResult | null {
  const uses = mediaUses(a.body).filter((u) => ctx.media[u.mediaId]);
  const clips = uses.filter((u) => ctx.media[u.mediaId].kind === 'clip');
  if (!clips.length) return null;
  const problems = clips
    .filter((u) => ctx.media[u.mediaId].bytes > rules.clipMaxBytes)
    .map((u) => ({ text: `${u.where} is ${mb(ctx.media[u.mediaId].bytes)} MB; the limit is ${mb(rules.clipMaxBytes)} MB` }));
  const offline = new Map(uses.filter((u) => ctx.media[u.mediaId].kind !== 'video').map((u) => [u.mediaId, ctx.media[u.mediaId].bytes]));
  const added = [...offline.values()].reduce((x, y) => x + y, 0);
  const sizes = [...new Set(clips.map((u) => u.mediaId))].map((id) => `${mb(ctx.media[id].bytes)} MB`);
  const list = sizes.length === 1 ? `Clip ${sizes[0]}` : `Clips ${sizes.slice(0, -1).join(', ')} and ${sizes[sizes.length - 1]}`;
  return result('clips', `${list}, under the ${mb(rules.clipMaxBytes)} MB limit. Adds ${mb(added)} MB to the Scout download`, `Each clip must be at most ${mb(rules.clipMaxBytes)} MB`, problems);
}

function videoCheck(a: ArticleContent, ctx: CheckContext): CheckResult | null {
  const videos = a.body.map((b, i) => ({ b, i })).filter((x) => x.b.type === 'video');
  if (!videos.length) return null;
  const hasSteps = a.body.some((b) => b.type === 'steps');
  const problems: Problem[] = [];
  for (const { b, i } of videos) {
    if (b.type !== 'video') continue;
    const where = blockName(a.body, i);
    const m = ctx.media[b.mediaId];
    if (m && !m.captionsSrc) problems.push({ text: `${where} has no captions` });
    if (!hasSteps && !b.stepsIn) problems.push({ text: `${where}: write its steps out in this article, or link the article that has them` });
  }
  return result('video', 'Video has captions, and its steps are written out', 'Each video needs captions and its steps written out', problems);
}

function linksCheck(a: ArticleContent, ctx: CheckContext): CheckResult {
  const problems: Problem[] = [];
  const test = (where: string, topicId: string, what = '') => {
    if (topicId === a.topicId) problems.push({ text: `${where}: ${what}${topicId} is this article` });
    else if (!ctx.liveTopics.has(topicId)) problems.push({ text: `${where}: ${what}${topicId} is not a live article` });
  };
  a.body.forEach((b, i) => {
    if (b.type === 'link') test(blockName(a.body, i), b.topicId);
    if (b.type === 'video' && b.stepsIn) test(blockName(a.body, i), b.stepsIn, 'its steps article ');
  });
  for (const r of a.related) test('Related', r);
  return result('links', 'Every link points to a live article', 'Every link must point to a live article', problems);
}

function safetyCheck(a: ArticleContent, rules: Rules): CheckResult | null {
  if (!a.safety) return null;
  const problems = rules.safetyLines[a.safety] ? [] : [{ text: `The ${a.safety} safety line has no approved wording yet` }];
  return result('safety', `The ${a.safety} safety line uses its approved wording`, 'The safety line needs approved wording', problems);
}

export function runChecks(a: ArticleContent, ctx: CheckContext): CheckResult[] {
  const rules = ctx.rules ?? RULES;
  return [
    labelsCheck(a, ctx),
    wordsCheck(a, rules),
    summaryCheck(a, rules),
    mediaCheck(a, ctx),
    linksCheck(a, ctx),
    clipsCheck(a, ctx, rules),
    videoCheck(a, ctx),
    safetyCheck(a, rules),
  ].filter((r): r is CheckResult => r !== null);
}

export const passes = (results: CheckResult[]) => results.every((r) => r.ok);
